import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, doc, getDoc, onSnapshot, orderBy, query, Timestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import ProfileDetail from "../components/ProfileDetail";
import { useVisualViewport } from "../lib/useVisualViewport";
import { useAuth } from "../auth/AuthProvider";
import {
  chooseDuelMatch, getDuelWord, joinDuel, leaveDuelLobby, passDuelWord, reactDuelSentence,
  resolveNoShowDuel, submitDuelAnswer, submitDuelClue, submitDuelGuess, submitDuelSentence,
} from "../lib/api";
import { friendly } from "../lib/errors";
import {
  DUEL_HB_HALF_MS, DUEL_TRIVIA_QUESTIONS, clueRevealsWord, duelGameMs, duelPhaseAt, isSingleWordClue, shuffledOptions, validateAcrostic,
} from "../lib/relayGame";
import { DUEL_REACTIONS, type ClueMsg, type DeckCard, type DuelAcroEntry, type DuelDoc } from "../lib/types";
import Countdown, { useNow } from "../components/Countdown";

export default function DuelRoom() {
  const { duelId } = useParams<{ duelId: string }>();
  const { uid } = useAuth();
  const navigate = useNavigate();
  const now = useNow(500);

  const [duel, setDuel] = useState<DuelDoc | null>(null);
  const [gone, setGone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decisionBusy, setDecisionBusy] = useState(false);
  const [oppCard, setOppCard] = useState<DeckCard | null>(null);
  const vv = useVisualViewport();

  // Errors show as a toast above the composer — clear them on their own so
  // they never linger over the game.
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error]);

  // Headbandz: the half-1 giver prefetches their first word DURING the lobby
  // countdown (the server allows the word fetch pre-start), so the half opens
  // with the word already on screen instead of a "getting your word…" beat.
  const hbPrefetch = useRef<{ word: string | null; next: string | null } | null>(null);
  const hbPrefetchStarted = useRef(false);
  useEffect(() => {
    if (!duel || !duelId || !uid || hbPrefetchStarted.current) return;
    if (duel.gameType !== "headbandz" || duel.state !== "live") return;
    if (duel.participants[0] !== uid) return;
    if (Date.now() >= (duel.startedAt?.toMillis() ?? 0)) return; // already started
    hbPrefetchStarted.current = true;
    let alive = true;
    void getDuelWord({ duelId })
      .then((r) => { if (alive) hbPrefetch.current = { word: r.word ?? null, next: r.next ?? null }; })
      .catch(() => { hbPrefetchStarted.current = false; }); // harmless — normal fetch covers it
    return () => { alive = false; };
  }, [duel, duelId, uid]);

  useEffect(() => {
    if (!duelId || !uid) return;
    return onSnapshot(
      doc(db, "duels", duelId),
      (snap) => {
        if (!snap.exists()) { setGone(true); return; }
        setDuel(snap.data() as DuelDoc);
      },
      () => setGone(true),
    );
  }, [duelId, uid]);

  // Mark myself present the moment I'm in the duel — this is how the no-show
  // check knows someone actually made it to the lobby.
  useEffect(() => {
    if (!duel || !duelId || !uid || duel.present?.[uid] === true) return;
    void updateDoc(doc(db, "duels", duelId), { [`present.${uid}`]: true }).catch(() => {});
  }, [duel, duelId, uid]);

  // If the lobby ends and my opponent never showed (no presence, no ready, no
  // activity), cancel the duel instead of playing a hollow 1-player game and
  // drop me back into the queue to find someone new.
  const noShowRef = useRef(false);
  useEffect(() => {
    if (!duel || !duelId || !uid || duel.state !== "live" || noShowRef.current) return;
    const startedAt = duel.startedAt?.toMillis() ?? 0;
    // Wait a solid beat past lobby end so a presence flag, a ready tap, or a
    // first answer has time to land before we call anyone a no-show.
    if (Date.now() < startedAt + 6000) return;
    const other = duel.participants.find((p) => p !== uid);
    if (!other) return;
    if (duel.present?.[other] === true || duel.ready?.[other] === true) return; // they showed
    noShowRef.current = true;
    // The server re-checks presence AND game activity; if the opponent is really
    // there it returns cancelled:false and the game just proceeds. On a real
    // cancel the state flips to "left"+noShow, which re-queues both sides below.
    void resolveNoShowDuel({ duelId })
      .then((r) => { if (!r.cancelled) noShowRef.current = false; })
      .catch(() => { noShowRef.current = false; });
  }, [duel, duelId, uid, now]);

  // A no-show cancel re-queues whoever is still on this screen (the one who
  // waited, and the wrongly-flagged player if a late race ever cancels them) —
  // nobody is left stranded in a game that no longer exists.
  useEffect(() => {
    if (duel?.state !== "left" || !duel.noShow) return;
    void joinDuel().catch(() => {});
    navigate("/relay/duel", { replace: true });
  }, [duel?.state, duel?.noShow, navigate]);

  // When BOTH players are ready, pull the lobby start in to ~5s (whichever
  // client notices first writes it; both writing the same ~now+5s is harmless).
  useEffect(() => {
    if (!duel || !duelId || duel.state !== "live") return;
    const started = duel.startedAt?.toMillis() ?? 0;
    const bothReady = duel.participants.every((p) => duel.ready?.[p] === true);
    if (bothReady && started - Date.now() > 6000) {
      const s = Date.now() + 5000;
      void updateDoc(doc(db, "duels", duelId), {
        startedAt: Timestamp.fromMillis(s),
        endsAt: Timestamp.fromMillis(s + duelGameMs(duel.gameType)),
      }).catch(() => {});
    }
  }, [duel, duelId]);

  // Someone backed out of the lobby. The person who left already headed home;
  // show the other a quick note, then send them home too.
  useEffect(() => {
    if (duel?.state !== "left" || duel.noShow) return; // no-show handled by its own flow
    if (duel.leftBy === uid) { navigate("/relay", { replace: true }); return; }
    const t = setTimeout(() => navigate("/relay", { replace: true }), 1800);
    return () => clearTimeout(t);
  }, [duel?.state, duel?.leftBy, duel?.noShow, uid, navigate]);

  if (gone) {
    return (
      <div className="screen center" style={{ paddingTop: 60 }}>
        <h1>1v1 over</h1>
        <p className="muted">This duel has wrapped up.</p>
        <button className="btn" onClick={() => navigate("/relay", { replace: true })}>Back to Relay</button>
      </div>
    );
  }
  if (duel?.state === "left" && duel.leftBy !== uid && !duel.noShow) {
    return (
      <div className="screen center" style={{ paddingTop: 60 }}>
        <div style={{ fontSize: 40 }}>👋</div>
        <h1>{duel.names?.[duel.leftBy ?? ""] ?? "Your opponent"} left</h1>
        <p className="muted">Taking you back to Relay…</p>
        <button className="btn" onClick={() => navigate("/relay", { replace: true })}>Back to Relay</button>
      </div>
    );
  }
  if (!duel || !uid || !duelId) return <div className="splash"><div className="spinner" /></div>;

  const me = uid;
  const other = duel.participants.find((p) => p !== me) ?? "";
  const otherName = duel.names[other] ?? "Them";
  const startedAt = duel.startedAt?.toMillis() ?? now;
  const phase = duelPhaseAt(duel.gameType, startedAt, now);

  // Scores per game type (client-side mirror of chooseDuelMatch's logic).
  const scores = computeScores(duel);
  const myScore = scores[me] ?? 0;
  const theirScore = scores[other] ?? 0;

  // Duel resolved server-side (other person passed / both chose).
  const resolved = duel.state !== "live" ? duel.state : null;

  // Clock says the game has started, but the opponent never showed (no presence,
  // no ready). Hold on a "waiting" screen rather than flashing a broken 1-player
  // game — the no-show effect either drops us in when they appear or re-queues us.
  const otherShowed = duel.present?.[other] === true || duel.ready?.[other] === true;
  const waitingForOpponent =
    duel.state === "live" && !!other && !otherShowed &&
    phase.phase !== "lobby" && phase.phase !== "decision";

  /** Choose and get on with your day — no waiting on the other person. A
   * mutual match arrives as a push + Matches badge, like Discover. A pass
   * simply disappears into the abyss. */
  async function decide(want: boolean) {
    if (decisionBusy || !duelId) return;
    setDecisionBusy(true);
    setError(null);
    try {
      await chooseDuelMatch({ duelId, want });
      navigate("/relay", { replace: true });
    } catch (e) {
      setError(friendly(e));
      setDecisionBusy(false);
    }
  }

  /** Post-game only: view their full profile and decide from there. */
  async function openOpponent() {
    try {
      const snap = await getDoc(doc(db, "users", other));
      const d = snap.data() ?? {};
      setOppCard({
        uid: other,
        displayName: (d.displayName as string) ?? otherName,
        age: (d.age as number) ?? 0,
        headline: (d.headline as string) ?? "",
        bio: (d.bio as string) ?? "",
        prompts: (d.prompts as { prompt: string; answer: string }[]) ?? [],
        photoURLs: (d.photoURLs as string[]) ?? [],
        locationName: "",
        distanceMiles: 0,
        boosted: false,
        compatibility: null,
        responsiveness: null,
      });
    } catch (e) {
      setError(friendly(e));
    }
  }

  const gameLabel = duel.gameType === "trivia" ? "Trivia"
    : duel.gameType === "headbandz" ? "Headbandz" : "Jumbled letters";

  // Lobby "Ready" handshake.
  const iAmReady = duel.ready?.[me] === true;
  const theyReady = duel.ready?.[other] === true;
  const readyLocked = startedAt - now <= 5000; // last 5s → can't ready anymore
  async function markReady() {
    if (iAmReady || readyLocked || !duelId) return;
    try {
      await updateDoc(doc(db, "duels", duelId), { [`ready.${me}`]: true });
    } catch (e) {
      setError(friendly(e));
    }
  }

  /** Back out during the lobby countdown — ends it for both and heads home. */
  async function leaveLobby() {
    if (!duelId) return;
    try {
      await leaveDuelLobby({ duelId });
    } catch (e) {
      setError(friendly(e));
    }
    navigate("/relay", { replace: true });
  }

  return (
    <div
      className={"chat-screen" + (vv.height ? " kb-open" : "")}
      style={vv.height ? { height: vv.height, transform: `translateY(${vv.top ?? 0}px)` } : undefined}
    >
      <header className="chat-header">
        <button
          className="chat-back"
          aria-label="Leave"
          onClick={() => {
            // Mid-lobby or mid-game, leaving must actually end the duel — a plain
            // navigate would just get undone by the auto-enter effect. On the
            // decision screen (or once resolved) it's a normal back.
            if (duel.state === "live" && phase.phase !== "decision") void leaveLobby();
            else navigate("/relay", { replace: true });
          }}
        >‹</button>
        <div className="chat-title">
          <div className="chat-name">1v1 · {gameLabel}</div>
          <div className="chat-sub">vs {otherName}</div>
        </div>
        {phase.phase === "hb" && (
          <span className="half-tag">
            Half {phase.half + 1}/2 · {duel.participants[phase.half] === me ? "you clue" : "you guess"}
          </span>
        )}
        {"questionEndsAt" in phase && <span className="pill"><Countdown endsAt={phase.questionEndsAt} /></span>}
        {"halfEndsAt" in phase && <span className="pill"><Countdown endsAt={phase.halfEndsAt} /></span>}
        {"endsAt" in phase && <span className="pill"><Countdown endsAt={phase.endsAt} /></span>}
      </header>

      {/* Compact scoreboard: scores flank the faces so the whole strip stays
          one short row — the game area below gets the vertical space. During
          Headbandz play this merges with the word bar into one pill (rendered
          by DuelHeadbandz), so skip it there. */}
      {!(phase.phase === "hb" && !waitingForOpponent) && (
        <div className="duel-vs">
          <div className="duel-player">
            <div className="p-score">{myScore}</div>
            <div className="duel-face">
              <div className="avatar-ring">☺</div>
              <div className="p-name">You</div>
            </div>
          </div>
          <span className="vs">VS</span>
          <div className="duel-player">
            <div className="duel-face">
              <div className="avatar-ring">☺</div>
              <div className="p-name">{otherName}</div>
            </div>
            <div className="p-score">{theirScore}</div>
          </div>
        </div>
      )}

      {phase.phase === "lobby" && (
        <div className="screen center" style={{ paddingTop: 12 }}>
          <div className="pulse-ring" />
          <h2 style={{ marginTop: 16 }}>Starting in <Countdown endsAt={startedAt} /></h2>
          <p className="muted">
            {duel.gameType === "trivia" && "5 questions, 15 seconds each. Answer on your own — best score wins."}
            {duel.gameType === "headbandz" && `Two ${DUEL_HB_HALF_MS / 1000}s halves. One gives one-word clues, the other guesses — then swap.`}
            {duel.gameType === "acro" && "3 rounds. Race to write a sentence from the letters — first valid one wins the round."}
          </p>
          <button
            className={"btn" + (iAmReady ? " btn-secondary" : "")}
            disabled={iAmReady || readyLocked}
            onClick={() => void markReady()}
          >
            {iAmReady ? "Ready ✓" : readyLocked ? "Starting…" : "I'm ready — skip the wait"}
          </button>
          <p className="muted" style={{ fontSize: 13 }}>
            {theyReady ? `${otherName} is ready ✓` : `Waiting on ${otherName}…`}
            {" "}Both ready and it starts in 5 seconds.
          </p>
          <button className="btn btn-ghost" style={{ marginTop: 4 }} onClick={() => void leaveLobby()}>
            Leave the lobby
          </button>
        </div>
      )}

      {waitingForOpponent && (
        <div className="screen center" style={{ paddingTop: 24 }}>
          <div className="pulse-ring" />
          <h2 style={{ marginTop: 16 }}>Waiting for {otherName} to join…</h2>
          <p className="muted">If they don't show in a moment, we'll find you someone new.</p>
        </div>
      )}

      {!waitingForOpponent && phase.phase === "trivia" && duel.gameType === "trivia" && (
        <DuelTrivia duel={duel} duelId={duelId} me={me} questionIndex={phase.questionIndex} setError={setError} />
      )}
      {!waitingForOpponent && phase.phase === "hb" && (
        <DuelHeadbandz
          duel={duel} duelId={duelId} me={me} half={phase.half} setError={setError}
          myScore={myScore} theirScore={theirScore} otherName={otherName}
          prefetch={hbPrefetch}
        />
      )}
      {!waitingForOpponent && (phase.phase === "acroWrite" || phase.phase === "acroReveal") && (
        <DuelAcro duel={duel} duelId={duelId} me={me} round={phase.round} writing={phase.phase === "acroWrite"} setError={setError} />
      )}

      {phase.phase === "decision" && (
        <div className="decision-card">
          {resolved === "passed" ? (
            <>
              <div style={{ fontSize: 40 }}>🌫️</div>
              <h2 style={{ margin: "8px 0 4px" }}>No match this time</h2>
              <p className="muted">One of you passed — that stays private. On to the next.</p>
              <button className="btn" onClick={() => navigate("/relay", { replace: true })}>Back to Relay</button>
            </>
          ) : (
            <>
              <h2 style={{ margin: "0 0 4px" }}>
                {myScore === theirScore ? "It's a tie!" : myScore > theirScore ? "You won 🏆" : `${otherName} won`}
              </h2>
              <p className="muted">
                Moment of truth — match with {otherName}? They never see your answer unless
                it's mutual. If you both say yes, you'll get a notification.
              </p>
              <button className="btn btn-secondary" disabled={decisionBusy} onClick={() => void openOpponent()}>
                View {otherName}'s profile
              </button>
              <button className="btn" disabled={decisionBusy} onClick={() => void decide(true)}>
                Match with {otherName} ⚡
              </button>
              <button className="btn btn-ghost" disabled={decisionBusy} onClick={() => void decide(false)}>
                Pass
              </button>
            </>
          )}
        </div>
      )}

      {/* Full profile (post-game) — like/pass here decides the duel, Discover-style. */}
      {oppCard && (
        <ProfileDetail
          profile={oppCard}
          onClose={() => setOppCard(null)}
          onLike={() => void decide(true)}
          onPass={() => void decide(false)}
        />
      )}

      {error && <div className="error">{error}</div>}
    </div>
  );
}

function computeScores(d: DuelDoc): Record<string, number> {
  const [a, b] = d.participants;
  if (d.gameType === "headbandz") return { [a]: d.hbScores?.[a] ?? 0, [b]: d.hbScores?.[b] ?? 0 };
  if (d.gameType === "acro") return { [a]: d.acroScores?.[a] ?? 0, [b]: d.acroScores?.[b] ?? 0 };
  const qs = d.questions ?? [];
  const answers = d.answers ?? {};
  const score = (u: string) => qs.reduce((n, q, i) => n + (answers[String(i)]?.[u] === q.correct ? 1 : 0), 0);
  return { [a]: score(a), [b]: score(b) };
}

// ---------------------------------------------------------------------------
// Trivia: same 5 questions, answered independently, 15s each.
// ---------------------------------------------------------------------------
function DuelTrivia({
  duel, duelId, me, questionIndex, setError,
}: {
  duel: DuelDoc; duelId: string; me: string; questionIndex: number;
  setError: (s: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const q = duel.questions?.[questionIndex];
  if (!q) return <div className="stub">Loading question…</div>;
  const myAnswer = duel.answers?.[String(questionIndex)]?.[me];
  const other = duel.participants.find((p) => p !== me) ?? "";
  const theyAnswered = duel.answers?.[String(questionIndex)]?.[other] != null;

  async function answer(opt: string) {
    if (busy || myAnswer != null) return;
    setBusy(true);
    setError(null);
    try {
      await submitDuelAnswer({ duelId, questionIndex, answer: opt });
    } catch (e) {
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="game-wrap" style={{ overflowY: "auto" }}>
      <div className="round-strip">
        Question {questionIndex + 1}/{DUEL_TRIVIA_QUESTIONS}
        {theyAnswered && <span> · {duel.names[other]} answered</span>}
      </div>
      <div className="question-card">
        <div className="q-cat">{q.category}</div>
        <div className="q-text">{q.question}</div>
        <div className="q-opts">
          {shuffledOptions(q, questionIndex).map((opt) => {
            const locked = myAnswer === opt;
            const showCorrect = myAnswer != null && opt === q.correct;
            return (
              <button
                key={opt}
                className={"q-opt" + (locked ? " q-opt-locked" : "") + (myAnswer != null && !locked && !showCorrect ? " q-opt-dim" : "")}
                style={showCorrect ? { borderColor: "#35d07f" } : undefined}
                disabled={busy || myAnswer != null}
                onClick={() => void answer(opt)}
              >
                {opt}
                {locked && <span className="muted"> · yours</span>}
                {showCorrect && <span style={{ color: "#35d07f" }}> ✓</span>}
              </button>
            );
          })}
        </div>
        {myAnswer == null
          ? <p className="muted" style={{ fontSize: 12 }}>First tap locks it — no take-backs.</p>
          : <p className="muted" style={{ fontSize: 12 }}>Locked. Next question when the clock turns.</p>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Headbandz: giver of half h = participants[h]; roles swap at the half.
// ---------------------------------------------------------------------------
function DuelHeadbandz({
  duel, duelId, me, half, setError, myScore, theirScore, otherName, prefetch,
}: {
  duel: DuelDoc; duelId: string; me: string; half: number;
  setError: (s: string | null) => void;
  myScore: number; theirScore: number; otherName: string;
  prefetch?: React.MutableRefObject<{ word: string | null; next: string | null } | null>;
}) {
  const giver = duel.participants[half];
  const isGiver = giver === me;
  // Cursors are per half — the server deals each half its own fresh word
  // sequence (no more inheriting the word your opponent was just looking at).
  const cursor = (half === 0 ? duel.hbCursor0 ?? duel.hbCursor : duel.hbCursor1) ?? 0;
  // Seed from the lobby prefetch so the half-1 giver opens with the word live.
  const [word, setWord] = useState<string | null>(() => (isGiver ? prefetch?.current?.word ?? null : null));
  const [draft, setDraft] = useState("");
  const [passing, setPassing] = useState(false);
  const [clues, setClues] = useState<ClueMsg[]>([]);
  // Optimistic clues/guesses — shown instantly, dropped once the server echoes
  // them back (kills the "did it send?" lag on a slow/cold round-trip).
  const [pending, setPending] = useState<{ id: string; kind: "clue" | "guess"; text: string }[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const qy = query(collection(db, "duels", duelId, "clues"), orderBy("sentAt", "asc"));
    return onSnapshot(qy, (snap) => {
      setClues(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ClueMsg, "id">) })));
    });
  }, [duelId]);

  // Drop optimistic messages the server has now confirmed.
  useEffect(() => {
    setPending((p) => {
      if (p.length === 0) return p;
      const mine = new Set(clues.filter((c) => c.senderId === me).map((c) => c.kind + " " + c.text));
      const next = p.filter((x) => !mine.has(x.kind + " " + x.text));
      return next.length === p.length ? p : next;
    });
  }, [clues, me]);

  const merged: ClueMsg[] = useMemo(() => {
    const mine = new Set(clues.filter((c) => c.senderId === me).map((c) => c.kind + " " + c.text));
    const extra = pending
      .filter((p) => !mine.has(p.kind + " " + p.text))
      .map((p) => ({ id: p.id, kind: p.kind, senderId: me, senderName: "", text: p.text, sentAt: null }));
    return [...clues, ...extra];
  }, [clues, pending, me]);

  const nextWordRef = useRef<string | null>(prefetch?.current?.next ?? null);
  const lastCursorRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isGiver) { setWord(null); nextWordRef.current = null; lastCursorRef.current = null; return; }
    // Only flip the display when the cursor actually ADVANCED (correct guess /
    // pass). On mount, keep whatever is showing (lobby prefetch or the
    // optimistic skip) — the fetch below confirms and refills the cache.
    const advanced = lastCursorRef.current !== null && lastCursorRef.current !== cursor;
    lastCursorRef.current = cursor;
    if (advanced) {
      setWord((w) => nextWordRef.current ?? w);
      nextWordRef.current = null;
    }
    let alive = true;
    void getDuelWord({ duelId })
      .then((r) => {
        if (!alive) return;
        setWord(r.word ?? null);
        nextWordRef.current = r.next ?? null;
      })
      .catch((e) => setError(friendly(e)));
    return () => { alive = false; };
  }, [isGiver, cursor, half, duelId, setError]);

  useEffect(() => {
    // Scroll only this pane (scrollIntoView also scrolls the document on iOS).
    const el = endRef.current?.parentElement;
    if (el) el.scrollTop = el.scrollHeight;
  }, [merged.length]);

  // When the keyboard opens the pane shrinks — keep it pinned to the latest
  // clue instead of leaving the newest messages hidden below the fold.
  useEffect(() => {
    const el = endRef.current?.parentElement;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => { el.scrollTop = el.scrollHeight; });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  async function send() {
    const t = draft.trim();
    if (!t) return;
    if (isGiver && !isSingleWordClue(t)) { setError("Clues must be a single word."); return; }
    if (isGiver && word && clueRevealsWord(t, word)) {
      setError("Nice try — you can't say the word itself!");
      return;
    }
    setError(null);
    // Show it immediately and clear the box so you can fire off the next one —
    // no waiting on the server round-trip.
    const kind: "clue" | "guess" = isGiver ? "clue" : "guess";
    const id = "pending-" + crypto.randomUUID();
    setPending((p) => [...p, { id, kind, text: t }]);
    setDraft("");
    try {
      if (isGiver) await submitDuelClue({ duelId, text: t });
      else await submitDuelGuess({ duelId, text: t });
    } catch (e) {
      setPending((p) => p.filter((x) => x.id !== id)); // failed — pull the optimistic bubble back
      setDraft(t);
      setError(friendly(e));
    }
  }

  return (
    <div className="game-wrap">
      {/* Everything above the clue stream lives in ONE pill: my score/face on
          the left, theirs on the right, and (for the giver) the word stacked
          under a small "their word" label in the middle. The "Half x/2" tag
          lives in the header next to the timer. */}
      <div className="duel-bar">
        <div className="duel-side">
          <div className="p-score">{myScore}</div>
          <div className="duel-face">
            <div className="avatar-ring">☺</div>
            <div className="p-name">You</div>
          </div>
        </div>
        {isGiver ? (
          <div className="duel-mid">
            <span className="hb-label">Their word · tap it to skip</span>
            {/* The word IS the skip button — keyed so each new word pops in. */}
            <button
              className="hb-word-btn"
              key={word ?? "loading"}
              disabled={passing || !word}
              /* keep the composer focused — skipping must not dismiss the keyboard */
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (passing) return;
                setPassing(true);
                // Flip to the cached next word IMMEDIATELY — the server call
                // catches up in the background (reverted if it fails).
                const prev = word;
                const optimistic = nextWordRef.current;
                if (optimistic) { setWord(optimistic); nextWordRef.current = null; }
                void passDuelWord({ duelId })
                  .catch((e) => { if (optimistic) setWord(prev); setError(friendly(e)); })
                  .finally(() => setPassing(false));
              }}
            >
              {word ?? <span className="muted" style={{ fontWeight: 600, fontSize: 12 }}>getting your word…</span>}
            </button>
          </div>
        ) : (
          <span className="vs">VS</span>
        )}
        <div className="duel-side">
          <div className="duel-face">
            <div className="avatar-ring">☺</div>
            <div className="p-name">{otherName}</div>
          </div>
          <div className="p-score">{theirScore}</div>
        </div>
      </div>
      <div className="pair-chat">
        {merged.map((c) =>
          c.kind === "reveal" ? (
            <div key={c.id} className="reveal-line">✓ Got it — “{c.text}”</div>
          ) : (
            <div key={c.id} className={"bubble-row" + (c.senderId === me ? " mine" : "")}>
              <div className="bubble bubble-sm">
                {c.kind === "guess" && <span className="muted">guess: </span>}
                {c.text}
              </div>
            </div>
          ),
        )}
        <div ref={endRef} />
      </div>
      <div className="chat-compose">
        <div className="compose-pill">
          {/* A real single-line input: placeholders can't wrap-and-clip like
              they do in a rows=1 textarea on iOS. */}
          <input
            type="text"
            value={draft}
            maxLength={100}
            enterKeyHint="send"
            autoCorrect="off"
            placeholder={isGiver ? "Your clue…" : "Your guess…"}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void send(); } }}
          />
          <button className="send-btn" aria-label="Send" disabled={!draft.trim()} onMouseDown={(e) => e.preventDefault()} onClick={() => void send()}>➤</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Acro ("Jumbled letters"): race to submit a valid acrostic; reveal + react.
// ---------------------------------------------------------------------------
function DuelAcro({
  duel, duelId, me, round, writing, setError,
}: {
  duel: DuelDoc; duelId: string; me: string; round: number; writing: boolean;
  setError: (s: string | null) => void;
}) {
  const letters = duel.acroLetters?.[String(round)] ?? [];
  const other = duel.participants.find((p) => p !== me) ?? "";
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [entries, setEntries] = useState<Record<string, DuelAcroEntry>>({});

  useEffect(() => {
    return onSnapshot(collection(db, "duels", duelId, "acroEntries"), (snap) => {
      const map: Record<string, DuelAcroEntry> = {};
      snap.docs.forEach((d) => { map[d.id] = d.data() as DuelAcroEntry; });
      setEntries(map);
    });
  }, [duelId]);

  const mine = entries[`${round}_${me}`];
  const theirs = entries[`${round}_${other}`];
  const winner = duel.acroWinners?.[String(round)];
  const valid = validateAcrostic(draft, letters);

  const winnerLine = useMemo(() => {
    if (!winner) return "Nobody landed a valid sentence this round.";
    return winner === me ? "You took the round 🏆" : `${duel.names[other]} took the round`;
  }, [winner, me, other, duel.names]);

  async function submit() {
    if (!valid || busy || mine) return;
    setBusy(true);
    setError(null);
    try {
      await submitDuelSentence({ duelId, round, sentence: draft.trim() });
      setDraft("");
    } catch (e) {
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  async function react(emoji: string) {
    try {
      await reactDuelSentence({ duelId, round, emoji });
    } catch (e) {
      setError(friendly(e));
    }
  }

  return (
    <div className="game-wrap" style={{ overflowY: "auto" }}>
      <div className="round-strip">Round {round + 1}/3 · {writing ? "write — first valid wins" : "reveal"}</div>
      <div className="letters-row">
        {letters.map((l, i) => <span key={i} className="letter-chip">{l.toUpperCase()}</span>)}
      </div>

      {writing ? (
        <div className="question-card">
          <div className="q-cat">Race!</div>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            One word per letter, in order. First valid sentence wins the round.
          </p>
          {mine ? (
            <>
              <p><b>Yours:</b> {mine.sentence}</p>
              <p className="muted" style={{ fontSize: 13 }}>
                {winner === me ? "You got there first 🏆" : winner ? "They beat you to it." : "Submitted."}
              </p>
            </>
          ) : (
            <>
              <textarea
                value={draft}
                maxLength={300}
                placeholder={letters.map((l) => l.toUpperCase() + "…").join(" ")}
                onChange={(e) => setDraft(e.target.value)}
              />
              <button className="btn" disabled={!valid || busy} onClick={() => void submit()}>
                {valid ? "Submit — go go go" : "Match every letter to submit"}
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="question-card">
          <div className="q-cat">{winnerLine}</div>
          {mine && (
            <p>
              <b>You:</b> “{mine.sentence}”
              {mine.reaction && <span className="reaction-tag">{mine.reaction}</span>}
            </p>
          )}
          {theirs ? (
            <>
              <p><b>{duel.names[other]}:</b> “{theirs.sentence}”</p>
              {!theirs.reaction ? (
                <div className="reaction-row">
                  {DUEL_REACTIONS.map((e) => (
                    <button key={e} className="reaction-btn" onClick={() => void react(e)}>{e}</button>
                  ))}
                </div>
              ) : (
                <p className="muted" style={{ fontSize: 13 }}>You reacted {theirs.reaction}</p>
              )}
            </>
          ) : (
            <p className="muted">{duel.names[other]} didn't finish a sentence 😅</p>
          )}
        </div>
      )}
    </div>
  );
}
